import './userCounts.less'
import { RootState } from '../../../../rootReducer'
import { getShowLoader } from '../../../../reducers/requestReducer'
import LoaderView from '../../../../components/Loader/loader'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import {
  ACTION_FETCH_USER_COUNTS_WIDGET,
  UserCountsWidget,
  fetchUserCountsWidget
} from '../../../users/usersSlice'
import { AccessControlUserTypes } from '../../../accessControl/types'
import {
  DATA_SOURCE_TYPES,
  FILTER_ACCOUNT_STATUSES,
  FILTER_USER_TYPE,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_ACCESS_CONTROL,
  URL_USERS,
  UserStatuses
} from '../../../../constants'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import { mockUserCountsWidgetData } from '../../renderer/utils/widgetMockdata'
import { CaretRight, CheckCircle, UserPlus, Users, XCircle } from 'phosphor-react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'
import { Typography } from '@lightbeamai/design-system'
import { Link } from 'react-router-dom'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  widget?: UserCountsWidget
  showLoader: boolean
}

interface DispatchProps {
  fetchUserCountsWidget: (params: { [UNIQUE_WIDGET_INDEX]?: string }) => void
}

type IProps = StateProps & DispatchProps & OwnProps

const UserCountsWidget = (props: IProps) => {
  const {
    widget,
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    fetchUserCountsWidget
  } = props
  const intl = useIntl()
  const {
    totalUsers = 0,
    activeUsers = 0,
    externalUsers = 0,
    guestUsers = 0,
    deletedUsers = 0,
    disabledUsers = 0
  } = widget || {}

  useEffect(() => {
    if (useMockData) return

    fetchUserCountsWidget({
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [useMockData, fetchUserCountsWidget])

  const getGovernanceUsersUrl = (filter?: { [key: string]: string | number | boolean }) => {
    return getUpdatedUrl({
      pathName: URL_ACCESS_CONTROL + URL_USERS,
      pageName: URL_USERS,
      pageQueryParams: {
        [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
        ...(filter || {})
      }
    })
  }
  const urlTotalUsers = getGovernanceUsersUrl()
  const urlActiveUsers = getGovernanceUsersUrl({ [FILTER_ACCOUNT_STATUSES]: UserStatuses.active })
  const urlExternalUsers = getGovernanceUsersUrl({
    [FILTER_USER_TYPE]: AccessControlUserTypes.external
  })
  const urlGuestUsers = getGovernanceUsersUrl({ [FILTER_USER_TYPE]: AccessControlUserTypes.guest })
  const urlDisabledUsers = getGovernanceUsersUrl({
    [FILTER_ACCOUNT_STATUSES]: UserStatuses.disabled
  })
  const urlDeletedUsers = getGovernanceUsersUrl({ [FILTER_ACCOUNT_STATUSES]: UserStatuses.deleted })

  return (
    <div className="lb-widget-card user-counts xs-p-16">
      <div className="lb-widget-card-widget" data-test-id="user-counts-widget">
        <div className="header-wrapper row">
          <Typography as="div" color="primary" variant="h6">
            {intl.formatMessage({ id: 'widgetUserCounts' })}
          </Typography>
          <Link to={urlTotalUsers} className="link-all-users row clickable" target="_blank">
            <Typography as="div" type="medium" variant="base" color="custom">
              {intl.formatMessage({ id: 'total' })}{' '}
              {intl.formatNumber(totalUsers, { notation: 'compact' })}
            </Typography>
            <div className="icon-box xs-ml-4">
              <CaretRight size="12" color="#3E21EE" weight="bold" />
            </div>
          </Link>
        </div>
        <Link className="widget-section clickable" to={urlActiveUsers} target="_blank">
          <div className="title-row row">
            <div className="icon-box xs-mr-4">
              <CheckCircle size={18} weight="fill" color="#3bb354" />
            </div>
            <Typography
              as="div"
              color="primary"
              className="title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'widgetUserCounts.active' })}
            </Typography>
          </div>
          <Typography
            as="div"
            color="primary"
            className="count xs-ml-8 "
            type="medium"
            variant="baseMinus2"
          >
            {intl.formatNumber(activeUsers, { notation: 'compact' })}
          </Typography>
        </Link>
        <Link className="widget-section clickable" to={urlExternalUsers} target="_blank">
          <div className="title-row row">
            <div className="icon-box xs-mr-4">
              <UserPlus size={18} weight="fill" color="#3e21ee" />
            </div>
            <Typography
              as="div"
              color="primary"
              className="title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'widgetUserCounts.external' })}
            </Typography>
          </div>
          <Typography
            as="div"
            color="primary"
            className="count xs-ml-8 "
            type="medium"
            variant="baseMinus2"
          >
            {intl.formatNumber(externalUsers, { notation: 'compact' })}
          </Typography>
        </Link>
        <Link className="widget-section clickable" to={urlGuestUsers} target="_blank">
          <div className="title-row row">
            <div className="icon-box xs-mr-4">
              <Users size={18} weight="fill" color="#ffae29" />
            </div>
            <Typography
              as="div"
              color="primary"
              className="title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'widgetUserCounts.guest' })}
            </Typography>
          </div>
          <Typography
            as="div"
            color="primary"
            className="count xs-ml-8 "
            type="medium"
            variant="baseMinus2"
          >
            {intl.formatNumber(guestUsers, { notation: 'compact' })}
          </Typography>
        </Link>
        <Link className="widget-section clickable" to={urlDisabledUsers} target="_blank">
          <div className="title-row row">
            <div className="icon-box xs-mr-4">
              <XCircle size={18} weight="fill" color="#ea630f" />
            </div>
            <Typography
              as="div"
              color="primary"
              className="title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'widgetUserCounts.disabled' })}
            </Typography>
          </div>
          <Typography
            as="div"
            color="primary"
            className="count xs-ml-8 "
            type="medium"
            variant="baseMinus2"
          >
            {intl.formatNumber(disabledUsers, { notation: 'compact' })}
          </Typography>
        </Link>
        <Link className="widget-section clickable" to={urlDeletedUsers} target="_blank">
          <div className="title-row row">
            <div className="icon-box xs-mr-4">
              <XCircle size={18} weight="fill" color="#e92014" />
            </div>
            <Typography
              as="div"
              color="primary"
              className="title"
              type="medium"
              variant="baseMinus2"
            >
              {intl.formatMessage({ id: 'widgetUserCounts.deleted' })}
            </Typography>
          </div>
          <Typography
            as="div"
            color="primary"
            className="count xs-ml-8 "
            type="medium"
            variant="baseMinus2"
          >
            {intl.formatNumber(deletedUsers, { notation: 'compact' })}
          </Typography>
        </Link>
        <LoaderView showLoader={showLoader} />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData ? mockUserCountsWidgetData : state.users.widgetUserCounts,
  showLoader: getShowLoader(ACTION_FETCH_USER_COUNTS_WIDGET)
})

const mapDispatchToProps: DispatchProps = { fetchUserCountsWidget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(UserCountsWidget)
